import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { colors } from '../theme';
import GradientButton from '../components/GradientButton';

export default function SignUpPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    displayName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    referralCode: '',
  });
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const update = (field: string, value: string) =>
    setForm((f) => ({ ...f, [field]: value }));

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (!agreed) {
      setError('Please agree to the Terms of Service.');
      return;
    }

    setLoading(true);
    try {
      const result = await createUserWithEmailAndPassword(auth, form.email, form.password);
      await updateProfile(result.user, { displayName: form.displayName });
      await setDoc(doc(db, 'users', result.user.uid), {
        displayName: form.displayName,
        email: form.email,
        phone: form.phone,
        role: 'customer',
        plan: 'free',
        createdAt: serverTimestamp(),
        referralCode: form.referralCode || null,
      });
      navigate('/home', { replace: true });
    } catch (err: any) {
      setError(getFriendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setGoogleLoading(true);
    setError('');
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const u = result.user;
      await setDoc(
        doc(db, 'users', u.uid),
        {
          displayName: u.displayName || '',
          email: u.email || '',
          phone: u.phoneNumber || '',
          role: 'customer',
          plan: 'free',
          createdAt: serverTimestamp(),
        },
        { merge: true }
      );
      navigate('/home', { replace: true });
    } catch (err: any) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setError('Google sign-up failed. Try again.');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: '#120B1E',
        overflowY: 'auto',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {/* Pink radial glow at top */}
      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height: 300,
        background: 'radial-gradient(ellipse at 50% -20%, rgba(192,38,160,0.28) 0%, rgba(123,47,190,0.12) 50%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div style={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '48px 20px 80px',
        position: 'relative',
      }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{
            width: 72, height: 72,
            borderRadius: 16,
            margin: '0 auto 14px',
            boxShadow: '0 6px 24px rgba(192,38,160,0.45)',
            overflow: 'hidden',
          }}>
            <img src="/logo-dark.png" alt="SageTech" style={{ width: 72, height: 72, objectFit: 'cover', display: 'block' }} />
          </div>
          <div style={{
            fontSize: 28, fontWeight: 800,
            background: 'linear-gradient(90deg, #E040A0, #9333EA)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginBottom: 6,
          }}>Create Account</div>
          <p style={{ color: 'rgba(255,255,255,0.5)', margin: 0, fontSize: 14 }}>
            Get expert tech help at your door
          </p>
        </div>

        <div
          style={{
            background: "rgba(255,255,255,0.07)",
            borderRadius: 20,
            padding: 28,
            boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
          }}
        >
          {/* Google signup */}
          <button
            onClick={handleGoogleSignUp}
            disabled={googleLoading}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              padding: '13px 20px',
              background: 'rgba(255,255,255,0.06)',
              border: '1.5px solid rgba(255,255,255,0.12)',
              borderRadius: 14,
              color: "#fff",
              fontSize: 15,
              fontWeight: 600,
              cursor: googleLoading ? 'not-allowed' : 'pointer',
              fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
              opacity: googleLoading ? 0.6 : 1,
              marginBottom: 20,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 48 48" fill="none">
              <path d="M44 24.6c0-1.4-.1-2.8-.4-4.1H24v7.8h11.3c-.5 2.6-2 4.8-4.1 6.3v5.2h6.6C41.7 36 44 30.7 44 24.6z" fill="#4285F4"/>
              <path d="M24 45c5.6 0 10.3-1.8 13.7-4.9l-6.6-5.2c-1.9 1.3-4.2 2-7.1 2-5.5 0-10.1-3.7-11.8-8.7H5.4v5.3C8.8 40.8 16 45 24 45z" fill="#34A853"/>
              <path d="M12.2 28.2c-.4-1.3-.7-2.7-.7-4.2s.2-2.9.7-4.2v-5.3H5.4C3.9 17.5 3 20.6 3 24s.9 6.5 2.4 9.5l6.8-5.3z" fill="#FBBC05"/>
              <path d="M24 10.7c3.1 0 5.8 1.1 8 3.1l5.9-5.9C34.3 4.6 29.6 2.7 24 2.7 16 2.7 8.8 6.9 5.4 13.5l6.8 5.3c1.7-5 6.3-8.1 11.8-8.1z" fill="#EA4335"/>
            </svg>
            {googleLoading ? 'Signing up…' : 'Sign up with Google'}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: 1, height: 1, background: colors.divider }} />
            <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 13 }}>or</span>
            <div style={{ flex: 1, height: 1, background: colors.divider }} />
          </div>

          <form onSubmit={handleSignUp} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Field label="Full Name">
              <input
                type="text"
                value={form.displayName}
                onChange={(e) => update('displayName', e.target.value)}
                placeholder="Jane Smith"
                required
                style={inputStyle}
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                placeholder="you@example.com"
                required
                style={inputStyle}
              />
            </Field>
            <Field label="Phone">
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => update('phone', e.target.value)}
                placeholder="(555) 000-0000"
                style={inputStyle}
              />
            </Field>
            <Field label="Password">
              <input
                type="password"
                value={form.password}
                onChange={(e) => update('password', e.target.value)}
                placeholder="Min. 6 characters"
                required
                style={inputStyle}
              />
            </Field>
            <Field label="Confirm Password">
              <input
                type="password"
                value={form.confirmPassword}
                onChange={(e) => update('confirmPassword', e.target.value)}
                placeholder="Repeat password"
                required
                style={inputStyle}
              />
            </Field>
            <Field label="Referral Code (optional)">
              <input
                type="text"
                value={form.referralCode}
                onChange={(e) => update('referralCode', e.target.value)}
                placeholder="Enter code if you have one"
                style={inputStyle}
              />
            </Field>

            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                cursor: 'pointer',
                marginTop: 4,
              }}
            >
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                style={{ marginTop: 2, accentColor: colors.primary }}
              />
              <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 13 }}>
                I agree to the{' '}
                <a href="https://sagetechapp.com/terms" target="_blank" rel="noreferrer" style={{ color: colors.primary }}>
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="https://sagetechapp.com/privacy" target="_blank" rel="noreferrer" style={{ color: colors.primary }}>
                  Privacy Policy
                </a>
              </span>
            </label>

            {error && (
              <p style={{ color: colors.error, fontSize: 14, margin: 0, textAlign: 'center' }}>{error}</p>
            )}

            <GradientButton type="submit" loading={loading} fullWidth style={{ marginTop: 4 }}>
              Create Account
            </GradientButton>
          </form>

          <p style={{ textAlign: 'center', margin: '18px 0 0', fontSize: 14, color: "rgba(255,255,255,0.6)" }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: colors.primary, fontWeight: 600, textDecoration: 'none' }}>
              Sign in
            </Link>
          </p>
        </div>

        <p style={{ textAlign: 'center', marginTop: 24, fontSize: 13, color: colors.textTertiary }}>
          Are you a tech?{' '}
          <a
            href="https://apps.apple.com/app/sagetechpro"
            target="_blank"
            rel="noreferrer"
            style={{ color: "rgba(255,255,255,0.6)", fontWeight: 600, textDecoration: 'none' }}
          >
            Download SageTech Pro →
          </a>
        </p>
      </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ color: "rgba(255,255,255,0.6)", fontSize: 13, display: 'block', marginBottom: 6 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '13px 14px',
  background: 'rgba(255,255,255,0.06)',
  border: '1.5px solid rgba(255,255,255,0.1)',
  borderRadius: 12,
  color: "#fff",
  fontSize: 15,
  fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
  outline: 'none',
  boxSizing: 'border-box',
};

function getFriendlyError(code: string): string {
  switch (code) {
    case 'auth/email-already-in-use': return 'An account with that email already exists.';
    case 'auth/invalid-email': return 'Invalid email address.';
    case 'auth/weak-password': return 'Password must be at least 6 characters.';
    default: return 'Sign up failed. Please try again.';
  }
}
